import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Fab from "@mui/material/Fab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import BugReportIcon from "@mui/icons-material/BugReport";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import useAutoschedulerDebugStore from "../../../../stores/autoschedulerDebugStore";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: "900px",
  maxHeight: "85%",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  overflow: "auto",
};

const fabStyle = {
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: 1000,
  backgroundColor: "#7C3AED",
  "&:hover": {
    backgroundColor: "#6D28D9",
  },
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: "16px" }}>
      {value === index && children}
    </div>
  );
}

function formatTime(seconds) {
  if (seconds === undefined || seconds === null) return "-";
  if (seconds < 0.001) return "<1ms";
  if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
  return `${seconds.toFixed(2)}s`;
}

function formatMetricValue(metric, value) {
  if (value === undefined || value === null) return "-";
  if (typeof value !== "number") return String(value);

  const percentMetrics = new Set([
    "labor_hours_delta_percent",
    "keyholder_coverage_percent",
    "minimum_staffing_coverage_percent",
    "role_distribution_match_percent",
  ]);
  const intMetrics = new Set([
    "total_shifts",
    "scheduled_people",
    "open_shifts",
    "total_qh_count",
    "understaffed_qh_count",
    "overstaffed_qh_count",
    "perfect_qh_count",
    "shifts",
  ]);

  if (percentMetrics.has(metric)) {
    return `${value.toFixed(1)}%`;
  }

  if (intMetrics.has(metric)) {
    return value.toLocaleString();
  }

  return value.toFixed(2);
}

function formatHoursValue(value) {
  if (value === undefined || value === null) return "-";
  if (typeof value !== "number") return String(value);
  return value.toFixed(2);
}

function formatPercentValue(value) {
  if (value === undefined || value === null) return "-";
  if (typeof value !== "number") return String(value);
  return `${value.toFixed(1)}%`;
}

function formatScoreValue(value) {
  if (value === undefined || value === null) return "-";
  if (typeof value !== "number") return String(value);
  return value.toFixed(1);
}

function AutoschedulerDebugModal() {
  const {
    isDebugModalOpen,
    debugStats,
    lastUpdated,
    openDebugModal,
    closeDebugModal,
  } = useAutoschedulerDebugStore();

  const [tabValue, setTabValue] = React.useState(0);

  // Don't render the FAB if there are no debug stats
  if (!debugStats) {
    return null;
  }

  const timings = debugStats?.timings || {};
  const counts = debugStats?.counts || {};
  const intermediateState = debugStats?.intermediate_state || {};
  const debugKpis = debugStats?.kpis || {};
  const employeeMetrics = debugStats?.employee_metrics || {};

  // Sort timings by duration descending
  const sortedTimings = Object.entries(timings)
    .filter(([key]) => key !== "total")
    .sort((a, b) => b[1] - a[1]);

  const hasKeyholderRoles = debugStats?.has_keyholder_roles !== false;
  const metricsComparisonRows = [
    { key: "score" },
    { key: "coverage_score" },
    { key: "budget_score" },
    { key: "hours" },
    { key: "shifts" },
    { key: "scheduled_people" },
    ...(hasKeyholderRoles ? [{ key: "keyholder_coverage_percent" }] : []),
    { key: "minimum_staffing_coverage_percent" },
    { key: "role_distribution_match_percent" },
    { key: "opening_closing_consistency_score" },
    { key: "total_shifts" },
    { key: "open_shifts" },
    { key: "open_shift_hours" },
    { key: "avg_shift_length_hours" },
    { key: "median_shift_length_hours" },
    { key: "scheduled_shift_hours" },
    { key: "scheduled_labor_hours" },
    { key: "forecasted_labor_hours" },
    { key: "labor_hours_delta" },
    { key: "labor_hours_delta_percent" },
    { key: "total_qh_count" },
    { key: "understaffed_qh_count" },
    { key: "overstaffed_qh_count" },
    { key: "perfect_qh_count" },
    { key: "understaffed_hours" },
    { key: "overstaffed_hours" },
    { key: "labor_variance_hours" },
  ];

  const employeeMetricHelp = {
    target_hours: "Target weekly hours. Inferred if missing using median of last 6 weeks.",
    scheduled_hours: "Total scheduled hours for the week.",
    target_delta: "Scheduled hours minus target hours.",
    target_source: "Stored means explicit target hours. Inferred uses last 6 weeks median.",
    target_specified: "Whether target hours were explicitly set.",
    employment_status: "Full-time if target or scheduled hours are at least 25.",
    week_to_week: "Percent of this week's shifts that match any shift seen in last 6 weeks.",
    shift_consistency: "Average start/end time deviation (hours) from this week's median.",
    target_score: "Target delta normalized to 0-100 (100 means match).",
    week_score: "Week-to-week consistency normalized to 0-100.",
    shift_score: "Shift consistency normalized to 0-100.",
    summary_score: "Average of available scores (target, week, shift).",
  };

  const renderHeaderWithHelp = (label, helpKey) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <span>{label}</span>
      {helpKey && (
        <Tooltip title={employeeMetricHelp[helpKey]} placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Tooltip>
      )}
    </Box>
  );

  const renderEmployeeMetricsTable = (title, rows) => {
    if (!rows.length) {
      return null;
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Target Hours", "target_hours")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Scheduled Hours", "scheduled_hours")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Delta", "target_delta")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Target Source", "target_source")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Target Specified", "target_specified")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Employment", "employment_status")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Week-to-Week Consistency", "week_to_week")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Shift Consistency", "shift_consistency")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Target Score", "target_score")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Week Score", "week_score")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Shift Score", "shift_score")}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {renderHeaderWithHelp("Summary Score", "summary_score")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.store_team_member_id}>
                  <TableCell>{row.employee_name}</TableCell>
                  <TableCell align="right">
                    {formatHoursValue(row.target_hours)}
                  </TableCell>
                  <TableCell align="right">
                    {formatHoursValue(row.scheduled_hours)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        row.target_hours_delta > 0
                          ? "green"
                          : row.target_hours_delta < 0
                          ? "red"
                          : "inherit",
                      fontWeight: row.target_hours_delta ? 600 : 400,
                    }}
                  >
                    {formatHoursValue(row.target_hours_delta)}
                  </TableCell>
                  <TableCell align="right">
                    {row.target_hours_inferred ? "Inferred" : "Stored"}
                  </TableCell>
                  <TableCell align="right">
                    {row.target_hours_specified ? "Yes" : "No"}
                  </TableCell>
                  <TableCell align="right">
                    {row.employment_status === "full_time" ? "Full-time" : "Part-time"}
                  </TableCell>
                  <TableCell align="right">
                    {formatPercentValue(row.week_to_week_consistency_percent)}
                  </TableCell>
                  <TableCell align="right">
                    {formatHoursValue(row.shift_consistency_hours)}
                  </TableCell>
                  <TableCell align="right">
                    {formatScoreValue(row.target_hours_score)}
                  </TableCell>
                  <TableCell align="right">
                    {formatScoreValue(row.week_to_week_consistency_score)}
                  </TableCell>
                  <TableCell align="right">
                    {formatScoreValue(row.shift_consistency_score)}
                  </TableCell>
                  <TableCell align="right">
                    {formatScoreValue(row.consistency_summary_score)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const calculateEmployeeSummary = (rows) => {
    if (!rows.length) {
      return null;
    }

    const scoreKeys = [
      "target_hours_score",
      "week_to_week_consistency_score",
      "shift_consistency_score",
      "consistency_summary_score",
    ];

    const summary = {};
    scoreKeys.forEach((key) => {
      const values = rows
        .map((row) => row[key])
        .filter((value) => typeof value === "number");
      if (!values.length) {
        summary[key] = null;
        return;
      }
      summary[key] = values.reduce((acc, value) => acc + value, 0) / values.length;
    });

    return summary;
  };

  const renderEmployeeSummaryComparison = (oldRows, newRows) => {
    const oldSummary = calculateEmployeeSummary(oldRows);
    const newSummary = calculateEmployeeSummary(newRows);

    if (!oldSummary && !newSummary) {
      return null;
    }

    const summaryRows = [
      { key: "target_hours_score", label: "Target Score" },
      { key: "week_to_week_consistency_score", label: "Week Score" },
      { key: "shift_consistency_score", label: "Shift Score" },
      { key: "consistency_summary_score", label: "Summary Score" },
    ];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Employee Summary (Avg Scores)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Old Schedule
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  New Schedule
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Change
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryRows.map(({ key, label }) => {
                const oldVal = oldSummary?.[key];
                const newVal = newSummary?.[key];
                const diff =
                  oldVal !== undefined && oldVal !== null && newVal !== undefined && newVal !== null
                    ? newVal - oldVal
                    : null;
                return (
                  <TableRow key={key}>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {label}
                    </TableCell>
                    <TableCell align="right">
                      {formatScoreValue(oldVal)}
                    </TableCell>
                    <TableCell align="right">
                      {formatScoreValue(newVal)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          diff > 0
                            ? "green"
                            : diff < 0
                            ? "red"
                            : "inherit",
                        fontWeight: diff ? 600 : 400,
                      }}
                    >
                      {diff !== null ? formatScoreValue(diff) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderScheduleScoreComparison = (
    oldMetrics,
    newMetrics,
    oldKpis,
    newKpis
  ) => {
    if (!oldMetrics && !newMetrics && !oldKpis && !newKpis) {
      return null;
    }

    const scoreRows = [
      { key: "score", label: "Total Score" },
      { key: "coverage_score", label: "Coverage Score" },
      { key: "budget_score", label: "Budget Score" },
      { key: "keyholder_coverage_percent", label: "Keyholder Coverage %" },
      { key: "minimum_staffing_coverage_percent", label: "Min Staffing Coverage %" },
      { key: "role_distribution_match_percent", label: "Role Distribution Match %" },
      { key: "opening_closing_consistency_score", label: "Open/Close Consistency" },
    ];

    const percentScoreKeys = new Set([
      "keyholder_coverage_percent",
      "minimum_staffing_coverage_percent",
      "role_distribution_match_percent",
    ]);

    const formatScoreCell = (key, value) =>
      percentScoreKeys.has(key)
        ? formatPercentValue(value)
        : formatScoreValue(value);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Schedule Scores
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Old Schedule
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  New Schedule
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Change
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scoreRows.map(({ key, label }) => {
                const oldVal = oldMetrics?.[key] ?? oldKpis?.[key];
                const newVal = newMetrics?.[key] ?? newKpis?.[key];
                const diff =
                  oldVal !== undefined && oldVal !== null && newVal !== undefined && newVal !== null
                    ? newVal - oldVal
                    : null;
                return (
                  <TableRow key={key}>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {label}
                    </TableCell>
                    <TableCell align="right">
                      {formatScoreCell(key, oldVal)}
                    </TableCell>
                    <TableCell align="right">
                      {formatScoreCell(key, newVal)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          diff > 0
                            ? "green"
                            : diff < 0
                            ? "red"
                            : "inherit",
                        fontWeight: diff ? 600 : 400,
                      }}
                    >
                      {diff !== null ? formatScoreCell(key, diff) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <>
      {/* Floating Action Button */}
      <Tooltip title="View Autoscheduler Debug Stats" placement="left">
        <Fab
          color="primary"
          aria-label="debug"
          sx={fabStyle}
          onClick={openDebugModal}
        >
          <BugReportIcon />
        </Fab>
      </Tooltip>

      {/* Debug Modal */}
      <Modal
        open={isDebugModalOpen}
        onClose={closeDebugModal}
        aria-labelledby="debug-modal-title"
      >
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                id="debug-modal-title"
                variant="h5"
                component="h2"
                sx={{ fontWeight: 600 }}
              >
                Autoscheduler Debug Stats
              </Typography>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {new Date(lastUpdated).toLocaleString()}
                </Typography>
              )}
            </Box>
            <IconButton onClick={closeDebugModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Timings" />
            <Tab label="Counts" />
            <Tab label="Intermediate State" />
            <Tab label="Score" />
            <Tab label="Employee Metrics" />
          </Tabs>

          {/* Timings Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Total Time: {formatTime(timings.total)}
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Duration
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      % of Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTimings.map(([step, duration]) => (
                    <TableRow key={step}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                        {step}
                      </TableCell>
                      <TableCell align="right">{formatTime(duration)}</TableCell>
                      <TableCell align="right">
                        {timings.total
                          ? `${((duration / timings.total) * 100).toFixed(1)}%`
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Counts Tab */}
          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(counts).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                        {key}
                      </TableCell>
                      <TableCell align="right">
                        {typeof value === "number"
                          ? value.toLocaleString()
                          : String(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Intermediate State Tab */}
          <TabPanel value={tabValue} index={2}>
            {/* Metrics Comparison */}
            {(intermediateState.old_metrics ||
              intermediateState.new_metrics ||
              debugKpis.old_sched ||
              debugKpis.new_sched) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Metrics Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Old Schedule
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          New Schedule
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Change
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metricsComparisonRows.map(({ key }) => {
                        const oldVal =
                          debugKpis.old_sched?.[key] ??
                          intermediateState.old_metrics?.[key];
                        const newVal =
                          debugKpis.new_sched?.[key] ??
                          intermediateState.new_metrics?.[key];
                        const diff =
                          oldVal !== undefined && newVal !== undefined
                            ? newVal - oldVal
                            : null;
                        return (
                          <TableRow key={key}>
                            <TableCell sx={{ fontFamily: "monospace" }}>
                              {key}
                            </TableCell>
                            <TableCell align="right">
                              {formatMetricValue(key, oldVal)}
                            </TableCell>
                            <TableCell align="right">
                              {formatMetricValue(key, newVal)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  diff > 0
                                    ? "green"
                                    : diff < 0
                                    ? "red"
                                    : "inherit",
                                fontWeight: diff !== 0 ? 600 : 400,
                              }}
                            >
                              {diff !== null ? formatMetricValue(key, diff) : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Historical Schedule Costs */}
            {intermediateState.historical_schedule_costs && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Historical Schedule Costs (Past 4 Weeks)
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {intermediateState.historical_schedule_costs.map((cost, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 2,
                        minWidth: 100,
                        textAlign: "center",
                        bgcolor:
                          cost === intermediateState.best_historical_cost
                            ? "#ECFDF5"
                            : "inherit",
                        borderColor:
                          cost === intermediateState.best_historical_cost
                            ? "#10B981"
                            : "inherit",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Week -{idx}
                      </Typography>
                      <Typography variant="h6">
                        {cost.toLocaleString()}
                      </Typography>
                      {cost === intermediateState.best_historical_cost && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#10B981", fontWeight: 600 }}
                        >
                          Best
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}

            {/* Other State */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Lock State
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        locked_all
                      </TableCell>
                      <TableCell align="right">
                        {String(intermediateState.locked_all ?? "-")}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        locked_dates
                      </TableCell>
                      <TableCell align="right">
                        {intermediateState.locked_dates?.join(", ") || "None"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Score Tab */}
          <TabPanel value={tabValue} index={3}>
            {renderScheduleScoreComparison(
              intermediateState.old_metrics,
              intermediateState.new_metrics,
              debugKpis.old_sched,
              debugKpis.new_sched
            )}
            {renderEmployeeSummaryComparison(
              employeeMetrics.old_sched || [],
              employeeMetrics.new_sched || []
            )}
            {!intermediateState.old_metrics &&
              !intermediateState.new_metrics &&
              !employeeMetrics.old_sched?.length &&
              !employeeMetrics.new_sched?.length && (
                <Typography variant="body2" color="text.secondary">
                  No score metrics available.
                </Typography>
              )}
          </TabPanel>

          {/* Employee Metrics Tab */}
          <TabPanel value={tabValue} index={4}>
            {renderEmployeeSummaryComparison(
              employeeMetrics.old_sched || [],
              employeeMetrics.new_sched || []
            )}
            {renderEmployeeMetricsTable(
              "Old Schedule",
              employeeMetrics.old_sched || []
            )}
            {renderEmployeeMetricsTable(
              "New Schedule",
              employeeMetrics.new_sched || []
            )}
            {!employeeMetrics.old_sched?.length &&
              !employeeMetrics.new_sched?.length && (
                <Typography variant="body2" color="text.secondary">
                  No employee metrics available.
                </Typography>
              )}
          </TabPanel>
        </Box>
      </Modal>
    </>
  );
}

export default AutoschedulerDebugModal;
